/*!
 * FrontAlign v1.1.0
 * (c) Eyruz Badalzada
 * Released under the MIT License
 * https://www.frontalign.dev
 */

const NAMESPACE = "FrontAlign";

const state = {
  enabled: false,
};

/**
 * Badge color for each log level,
 * Safe in Node too — `%c` is just ignored there.
 */
const LEVELS = {
  log: { label: "LOG", color: "#3b82f6" }, // blue
  warn: { label: "WARN", color: "#f59e0b" }, // amber
  error: { label: "ERROR", color: "#ef4444" }, // red
};

const NAMESPACE_STYLE =
  "background:#0f172a;color:#f8fafc;font-weight:600;padding:2px 8px;border-radius:4px 0 0 4px;";

const badgeStyle = (color) =>
  `background:${color};color:#0f172a;font-weight:700;padding:2px 8px;border-radius:0 4px 4px 0;`;

const format = (level) => [
  `%c ${NAMESPACE} %c ${LEVELS[level].label} `,
  NAMESPACE_STYLE,
  badgeStyle(LEVELS[level].color),
];

/**
 * Shared emitter for log/warn/error,
 * Guarded calls only print in debug mode.
 */
function emit(method, level, guarded, args) {
  if (guarded && !state.enabled) return;
  console[method](...format(level), ...args);
}

const Debug = {
  // Enables debug mode.
  enable() {
    state.enabled = true;
  },

  // Disables debug mode.
  disable() {
    state.enabled = false;
  },

  // Sets debug mode from a boolean.
  set(value) {
    state.enabled = Boolean(value);
  },

  // Read-only access to the current state.
  get isEnabled() {
    return state.enabled;
  },

  /**
   * Info messages for early-exit guard clauses,
   * Only shown when debug mode is on.
   */
  log(...args) {
    emit("log", "log", true, args);
  },

  /**
   * Warnings for non-critical issues,
   * Only shown when debug mode is on.
   */
  warn(...args) {
    emit("warn", "warn", true, args);
  },

  /**
   * Errors — always shown, debug mode,
   * or not. Real problems shouldn't hide.
   */
  error(...args) {
    emit("error", "error", false, args);
  },

  // Groups messages while debug mode is on.
  group(label) {
    if (!state.enabled) return;
    console.group(...format("log"), label);
  },

  // Closes the current console group. 
  groupEnd() {
    if (state.enabled) console.groupEnd();
  },
};

export default Debug;
